import { useEffect, useState } from 'react';
import type { EntitlementResponse } from './types';

export interface UseTokenResult {
  status: 'loading' | 'valid' | 'invalid';
  token: string | null;
  user: EntitlementResponse['user'];
  entitlement: EntitlementResponse['entitlement'];
  reason: string | null;
}

/**
 * Reads the JWT from `?token=` (or `?t=`) on first mount, calls the
 * entitlement-checker Worker, and returns the validation result.
 *
 * When `endpoint` is an empty string, token validation is bypassed
 * (local dev only). The token is still parsed so personalisation works.
 */
export function useToken(options: { endpoint: string; productId: string }): UseTokenResult {
  const [state, setState] = useState<UseTokenResult>({
    status: 'loading',
    token: null,
    user: undefined,
    entitlement: undefined,
    reason: null,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token') ?? params.get('t');

    if (!token) {
      setState({
        status: 'invalid',
        token: null,
        user: undefined,
        entitlement: undefined,
        reason: 'missing_token',
      });
      return;
    }

    if (!options.endpoint) {
      const firstName = params.get('first_name');
      setState({
        status: 'valid',
        token,
        user: { email: params.get('email') ?? 'local@aicores.dev', first_name: firstName },
        entitlement: { granted_at: Date.now(), source: 'local_dev' },
        reason: null,
      });
      return;
    }

    fetch(`${options.endpoint}?product_id=${encodeURIComponent(options.productId)}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const body = (await res.json()) as EntitlementResponse;
        if (body.valid) {
          setState({
            status: 'valid',
            token,
            user: body.user,
            entitlement: body.entitlement,
            reason: null,
          });
        } else {
          setState({
            status: 'invalid',
            token,
            user: undefined,
            entitlement: undefined,
            reason: body.reason ?? 'invalid_token',
          });
        }
      })
      .catch(() => {
        setState({
          status: 'invalid',
          token,
          user: undefined,
          entitlement: undefined,
          reason: 'network_error',
        });
      });
  }, [options.endpoint, options.productId]);

  return state;
}
